import React, { useState, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { PanelStateless } from '@atlaskit/panel';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import GenericMessage from '@atlassian/bitbucket-generic-message';

import Loading from 'src/components/loading';
import { GpgKeysCommitVerification } from 'src/components/settings/gpg-keys/components/commit-verification';
import { Commit as CommitType } from 'src/components/types';
import { CommitList } from 'src/containers/commit-list';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import common from 'src/i18n/common';
import * as styles from 'src/sections/global/components/page.style';
import CommitListBuildStatus from 'src/sections/repository/sections/commit-list/components/commit-list-build-status/commit-list-build-status';
import { SyncStrategy } from 'src/types/pull-request';

import { SyncBranchDialog } from '../../sections/branches/components/dialogs/sync-branch-dialog-functional';
import { renderApproval } from '../../sections/commit-list/components/commit-list-icons';

import RepositoryBranchSyncInfo, {
  BranchSyncInfoProps,
} from './branch-sync-info';
import messages from './commit-list.i18n';
import * as commitStyles from './commit-list.style';

export type RepositoryCommitListProps = {
  isLoading: boolean;
  hasError: boolean;
  commits: CommitType[];
  syncStrategies?: SyncStrategy[];
  hasMore: boolean;
  retryAction: () => void;
  fetchMoreAction: () => void;
};

export type CommitTableProps = {
  repositoryFullSlug: string;
} & RepositoryCommitListProps;

type Props = {
  errorMessage?: string;
  renderBuildStatus?: (commit: CommitType) => React.ReactNode;
  repositoryFullSlug: string;
  commitListDetails: RepositoryCommitListProps;
  branchSyncInfoDetails?: BranchSyncInfoProps;
  startExpanded?: boolean;
  analyticsParentPage?: string;
};

const CommitCountMessage = ({
  commits,
  hasMore,
  hasError,
}: {
  commits?: number;
  hasMore: boolean;
  hasError: boolean;
}) => (
  <commitStyles.PanelHeader>
    <commitStyles.CommitListHeading>
      <FormattedMessage
        {...messages.commitListTitle}
        values={{
          hasError: hasError.toString(),
          hasMore: hasMore.toString(),
          count: commits || 0,
        }}
      />
    </commitStyles.CommitListHeading>
    {hasError && (
      <Tooltip
        tag="span"
        content={<FormattedMessage {...messages.commitListGenericError} />}
      >
        <WarningIcon primaryColor={token('color.icon.warning')} label="" />
      </Tooltip>
    )}
  </commitStyles.PanelHeader>
);

const CommitLoadingMessage = () => (
  <styles.PanelHeader>
    <FormattedMessage {...messages.commitListLoading} />
  </styles.PanelHeader>
);

export const LoadMoreButton = ({
  isLoading,
  fetchMoreAction,
}: {
  isLoading?: boolean;
  fetchMoreAction: () => void;
}) => {
  return (
    <CustomThemeButton
      isLoading={isLoading}
      appearance="subtle"
      onClick={fetchMoreAction}
      theme={(currentTheme, props) => ({
        ...currentTheme(props),
        buttonStyles: {
          ...currentTheme(props).buttonStyles,
          display: 'block',
          margin: '0 auto',
        },
      })}
    >
      <FormattedMessage {...messages.commitListLoadMore} />
    </CustomThemeButton>
  );
};

/* CommitTable includes: loading indicator when making commits request,
table of commits (with build status), load more button, retry warning with action
if commits GET request fails, and an optional track in view wrapper.
*/
export const CommitTable = ({
  repositoryFullSlug,
  isLoading,
  hasError,
  commits,
  hasMore,
  retryAction,
  fetchMoreAction,
}: CommitTableProps): JSX.Element => {
  const intl = useIntl();

  const renderBuildStatus = useCallback(
    (commit: CommitType) => (
      <CommitListBuildStatus
        commit={commit}
        repositoryFullSlug={repositoryFullSlug}
      />
    ),
    [repositoryFullSlug]
  );

  const renderVerificationStatus = useCallback(
    (commit: CommitType) => <GpgKeysCommitVerification commit={commit} />,
    []
  );

  const renderAfterMessage = (commit: CommitType) => {
    return <>{renderApproval(commit, intl)}</>;
  };

  return (
    <>
      {commits.length > 0 && (
        <CommitList
          commits={commits}
          renderBuildStatus={renderBuildStatus}
          showHeaders
          repositoryFullSlug={repositoryFullSlug}
          renderAfterMessage={renderAfterMessage}
          renderVerificationStatus={renderVerificationStatus}
        />
      )}

      {/* This happens during initial load while we're unsure if we have more yet. */}
      {isLoading && !hasMore && <Loading />}

      {hasMore && (
        <LoadMoreButton
          isLoading={isLoading}
          fetchMoreAction={fetchMoreAction}
        />
      )}

      {hasError && !isLoading && (
        <GenericMessage
          iconType="warning"
          title={<FormattedMessage {...common.errorHeading} />}
        >
          <CustomThemeButton appearance="link" onClick={retryAction}>
            <FormattedMessage {...common.tryAgain} />
          </CustomThemeButton>
        </GenericMessage>
      )}
    </>
  );
};

/* RepositoryCommitListPanel includes: expandable panel with a header and
the table of commits. It also includes info about number of commits behind source branch,
and the option to sync the branch.
*/
const RepositoryCommitListPanel = ({
  repositoryFullSlug,
  branchSyncInfoDetails,
  commitListDetails,
  startExpanded = false,
  analyticsParentPage,
}: Props): JSX.Element => {
  const [isPanelExpanded, setIsPanelExpanded] = useState(startExpanded);

  const mergeStatusStateMessageDisabled = useStatsigGate(
    StatsigFeatureKeys.mergeStatusStateMessageDisabled
  );

  const {
    isLoading,
    hasError,
    commits,
    hasMore,
    syncStrategies,
    retryAction,
    fetchMoreAction,
  } = commitListDetails;

  const Header = isLoading ? (
    <CommitLoadingMessage />
  ) : (
    <commitStyles.PanelHeaderWrapper>
      <CommitCountMessage
        commits={commits.length}
        hasMore={hasMore}
        hasError={hasError}
      />
      {!isPanelExpanded && branchSyncInfoDetails && (
        <commitStyles.BranchSyncInfoWrapper>
          <RepositoryBranchSyncInfo
            {...branchSyncInfoDetails}
            appearance="inline"
            hideBranchSyncButton={mergeStatusStateMessageDisabled}
          />
        </commitStyles.BranchSyncInfoWrapper>
      )}
    </commitStyles.PanelHeaderWrapper>
  );

  return (
    <PanelStateless
      header={Header}
      isExpanded={isPanelExpanded}
      onChange={setIsPanelExpanded}
    >
      {branchSyncInfoDetails && (
        <RepositoryBranchSyncInfo {...branchSyncInfoDetails} />
      )}
      <SyncBranchDialog
        syncStrategies={syncStrategies}
        analyticsParentPage={analyticsParentPage}
      />
      <CommitTable
        repositoryFullSlug={repositoryFullSlug}
        isLoading={isLoading}
        hasError={hasError}
        commits={commits}
        hasMore={hasMore}
        retryAction={retryAction}
        fetchMoreAction={fetchMoreAction}
      />
    </PanelStateless>
  );
};

export default React.memo(RepositoryCommitListPanel);
